import { useCallback, useEffect, useRef, useState } from "react";
import { io } from "socket.io-client";
import axios from "axios";
import { APP_PATH } from "../api/backendPath";
import {
  ensureFreshAccessToken,
  getAccessToken,
  getTokenUserId,
  isAuthError,
  isTokenFresh,
} from "../utils/socketAuth";
import {
  ADMIN_CHAT_READ_EVENT,
  isAdminSupportMessage,
} from "../utils/chatEvents";

const SOCKET_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";
const MAX_RETRY_DELAY_MS = 30000;
const ACK_TIMEOUT_MS = 15000;

const sameId = (a, b) => a != null && b != null && String(a) === String(b);

/**
 * Socket for the provider's support chat with the admin team ("Chat" in the sidebar).
 *
 * - Only messages of the support room are shown. Every chat message for the provider (patient
 *   chats too) arrives on the same personal channel, so they are filtered by room.
 * - Messages are marked read as they arrive while the page is open, and the sidebar badge is told
 *   to re-sync (ADMIN_CHAT_READ_EVENT).
 * - The access token is refreshed before it expires and on a refused reconnect: the gateway
 *   authenticates every emit with the handshake token, so an expired one silently drops sends.
 */
export default function useSupportChat() {
  const [messages, setMessages] = useState([]);
  const [chatRoomId, setChatRoomId] = useState(null);
  const [chatLoading, setChatLoading] = useState(true);
  const socketRef = useRef(null);
  const roomIdRef = useRef(null);
  const markReadTimerRef = useRef(null);
  const reconnectRef = useRef(null);

  const markRead = useCallback((rid) => {
    const token = getAccessToken();
    if (!rid || !token) return;
    axios
      .post(
        `${process.env.REACT_APP_API_URL}${APP_PATH.consultationChatMarkRead}`,
        { room_id: rid },
        { headers: { Authorization: `Bearer ${token}` } },
      )
      .then(() => window.dispatchEvent(new CustomEvent(ADMIN_CHAT_READ_EVENT)))
      .catch(() => {});
  }, []);

  // Several messages in a burst need a single mark-read.
  const markReadSoon = useCallback(
    (rid) => {
      if (markReadTimerRef.current) clearTimeout(markReadTimerRef.current);
      markReadTimerRef.current = setTimeout(() => {
        markReadTimerRef.current = null;
        markRead(rid);
      }, 400);
    },
    [markRead],
  );

  useEffect(() => {
    const token = getAccessToken();
    if (!token) {
      setChatLoading(false);
      return undefined;
    }
    const myUserId = getTokenUserId(token);
    const socket = io(SOCKET_URL, {
      query: { token },
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
    });
    socketRef.current = socket;
    let disposed = false;
    let retryTimer = null;
    let refreshTimer = null;
    let retries = 0;

    // Reopen the connection with a valid token (refreshing it when the current one is stale).
    const reconnectWithFreshToken = async ({ force = false } = {}) => {
      const handshakeToken = socket.io.opts.query?.token;
      const fresh = await ensureFreshAccessToken(force ? handshakeToken : null);
      if (disposed || !fresh) return false;
      if (fresh !== handshakeToken || !socket.connected) {
        socket.io.opts.query = { token: fresh };
        if (socket.connected) socket.disconnect();
        socket.connect();
      }
      return true;
    };
    reconnectRef.current = reconnectWithFreshToken;

    // socket.io does not retry a refused handshake or a server-side close by itself.
    const scheduleManualReconnect = (authRejected) => {
      if (disposed || retryTimer) return;
      const delay = Math.min(MAX_RETRY_DELAY_MS, 1000 * 2 ** retries);
      retries += 1;
      retryTimer = setTimeout(() => {
        retryTimer = null;
        if (!disposed && !socket.connected)
          reconnectWithFreshToken({ force: authRejected });
      }, delay);
    };

    // Refresh shortly before the handshake token expires.
    const scheduleTokenRefresh = () => {
      if (refreshTimer) clearTimeout(refreshTimer);
      const handshakeToken = socket.io.opts.query?.token;
      let expMs = 0;
      try {
        const part = String(handshakeToken).split(".")[1];
        const payload = JSON.parse(
          atob(part.replace(/-/g, "+").replace(/_/g, "/")),
        );
        if (payload.exp) expMs = payload.exp * 1000;
      } catch (e) {}
      if (!expMs) return;
      const delay = Math.max(5000, expMs - Date.now() - 30000);
      refreshTimer = setTimeout(() => {
        refreshTimer = null;
        reconnectWithFreshToken({ force: true });
      }, delay);
    };

    socket.on("connect", () => {
      retries = 0;
      scheduleTokenRefresh();
      // Support chat with the whole admin team: the server resolves the room (no admin id).
      socket.emit("get_or_create_room", { support_chat: true }, (ack) => {
        if (disposed) return;
        if (!ack?.success || !ack?.room_id) {
          setChatLoading(false);
          return;
        }
        const rid = ack.room_id;
        roomIdRef.current = rid;
        setChatRoomId(rid);
        socket.emit("join_room", { room_id: rid });
        socket.emit(
          "get_messages",
          { room_id: rid, page: 1, limit: 100 },
          (msgAck) => {
            if (disposed) return;
            if (msgAck?.success && Array.isArray(msgAck.messages))
              setMessages(msgAck.messages);
            setChatLoading(false);
          },
        );
        markRead(rid);
      });
    });

    socket.on("connect_error", (err) => {
      if (!socket.active) scheduleManualReconnect(isAuthError(err));
    });

    socket.on("disconnect", (reason) => {
      if (reason === "io server disconnect") scheduleManualReconnect(false);
    });

    socket.io.on("reconnect_attempt", () => {
      const latest = getAccessToken();
      if (latest) socket.io.opts.query = { token: latest };
    });

    socket.on("new_message", (data) => {
      const rid = roomIdRef.current;
      // Only this support conversation. Before the room id is known, the support room is the one
      // tagged "admin_provider"; patient chats carry no context.
      const isSupportRoom =
        rid != null ? sameId(data?.room_id, rid) : isAdminSupportMessage(data);
      if (!isSupportRoom || data?.message_id == null) return;
      if (sameId(data.sender_id, myUserId)) return;
      setMessages((prev) => {
        if (prev.some((m) => sameId(m.id, data.message_id))) return prev;
        return [
          ...prev,
          {
            id: data.message_id,
            room_id: data.room_id,
            sender_id: data.sender_id,
            message: data.message,
            message_type: data.message_type || "text",
            metadata: data.metadata || null,
            created_at: data.timestamp || new Date().toISOString(),
          },
        ];
      });
      // The provider is looking at the conversation: it is read on arrival.
      markReadSoon(rid ?? data.room_id);
    });

    return () => {
      disposed = true;
      if (retryTimer) clearTimeout(retryTimer);
      if (refreshTimer) clearTimeout(refreshTimer);
      if (markReadTimerRef.current) {
        clearTimeout(markReadTimerRef.current);
        markReadTimerRef.current = null;
      }
      try {
        if (roomIdRef.current)
          socket.emit("leave_room", { room_id: roomIdRef.current });
      } catch (e) {}
      socket.removeAllListeners();
      socket.disconnect();
      socketRef.current = null;
      roomIdRef.current = null;
      reconnectRef.current = null;
    };
  }, [markRead, markReadSoon]);

  /**
   * Send over the socket without dropping the message: waits while the socket reconnects and
   * retries when the server does not confirm (an expired token is refreshed first).
   */
  const sendSocketMessage = useCallback(function emit(
    payload,
    { waits = 0, sends = 0 } = {},
  ) {
    const socket = socketRef.current;
    if (!socket) return;
    if (!socket.connected) {
      // Up to ~20 s for a reconnect.
      if (waits < 40)
        setTimeout(() => emit(payload, { waits: waits + 1, sends }), 500);
      return;
    }
    socket
      .timeout(ACK_TIMEOUT_MS)
      .emit("send_message", payload, (err, ack) => {
        if (!err && ack?.success) return;
        if (sends >= 3) return;
        const retry = () =>
          setTimeout(() => emit(payload, { waits, sends: sends + 1 }), 1000);
        // No answer at all: the gateway refuses emits once the handshake token has expired.
        const handshakeToken = socket.io.opts.query?.token;
        if (err && !isTokenFresh(handshakeToken) && reconnectRef.current) {
          reconnectRef.current({ force: true }).finally(retry);
        } else {
          retry();
        }
      });
  }, []);

  return {
    messages,
    setMessages,
    chatRoomId,
    chatLoading,
    sendSocketMessage,
  };
}
